import { call, delay, put, select, take, takeLatest } from 'redux-saga/effects';

import { monolithApiUrl } from '../../../config/app';
import { getDefaultExpirationDateInUTC } from '../../../constants/bucket';
import { BULK_SEND_CREATED_MESSAGE, EXPIRATION_OUTDATED_MESSAGE } from '../../../constants/createSendSubmit';
import { HUBSPOT } from '../../../constants/integrations';
import { endpoints, routes } from '../../../constants/routing';
import { CONTACT, CRM_TYPE } from '../../../constants/shipping';
import {
  ICreateEngagementResponse,
  ICreateOneLinkSendResponse,
  IOneLinkEngagementCandidate,
  TEngagementCandidate,
} from '../../../types/bucket';
import { ICreateSendCrmLink } from '../../../types/createSendCrm';
import { CreateSendMethodEnum } from '../../../types/createSendFlow';
import { IOpportunityResponse } from '../../../types/crm';
import { IResponse } from '../../../types/http';
import { isBulkResponse, ISendsV2Request, TSendsV2Response } from '../../../types/sendsV2';
import { IApiError } from '../../../types/shell';
import { buildBulkSendTitle } from '../../../utils/bulkSendTitle';
import { isDateInFuture } from '../../../utils/date';
import { mapEngagementCandidateToRequest } from '../../../utils/engagementRequest';
import notification from '../../../utils/notification';
import * as IntegrationsActions from '../../actions/integrations';
import * as ModalActions from '../../actions/modals';
import { routerProxyPush } from '../../actions/shell';
import { AuthRequest } from '../../sagas/helpers';
import { selectCreateSendLinkCandidate } from '../../selectors/createSendLinkCandidate';
import { selectCreateSendLocationCandidate } from '../../selectors/createSendLocationCandidate';
import {
  selectCreateSendRecipientsCandidate,
  selectCsBulkTitleCampaignName,
} from '../../selectors/createSendRecipientsCandidate';
import { selectCrmOpportunities } from '../../selectors/integrations';
import { selectCurrentOrganizationCRMType } from '../../selectors/organizations';
import { setCsCrmOpportunity } from '../actions/crm';
import { resetCreateSend } from '../actions/root';
import * as SubmitActions from '../actions/submit';
import { selectCsCrmOpportunityTarget } from '../selectors/crm';
import { selectCreateSendMethod } from '../selectors/method';

const isExpirationAcceptable = (candidate: IOneLinkEngagementCandidate): boolean => {
  const expirationDate = candidate.expiration_date;

  return !expirationDate || isDateInFuture(expirationDate);
};

export const IN_PROGRESS_ERROR_FRAGMENT = 'already in progress';

export const SENDS_V2_RETRY_DELAY_MS = 1500;

export const SENDS_V2_MAX_RETRIES = 3;

export const isEngagementInProgressError = (message?: string): boolean =>
  Boolean(message && message.includes(IN_PROGRESS_ERROR_FRAGMENT));

type TSubmitPlan =
  | { kind: 'location'; engagement: TEngagementCandidate; metadata: Record<string, boolean> }
  | { kind: 'link'; engagement: IOneLinkEngagementCandidate; metadata: Record<string, boolean> }
  | { kind: 'recipients'; engagement: ISendsV2Request; metadata: Record<string, boolean> };

export function* resolveSubmitPlan(): Generator<any, TSubmitPlan | null, any> {
  const method = yield select(selectCreateSendMethod);

  if (method === CreateSendMethodEnum.Location) {
    const engagement = yield select(selectCreateSendLocationCandidate);

    return { engagement, metadata: { isDirectSend: true }, kind: 'location' };
  }

  if (method === CreateSendMethodEnum.Link) {
    const engagement = yield select(selectCreateSendLinkCandidate);

    return { engagement, metadata: { isOneLink: true }, kind: 'link' };
  }

  if (method === CreateSendMethodEnum.Recipients) {
    const engagement = yield select(selectCreateSendRecipientsCandidate);

    return { engagement, metadata: {}, kind: 'recipients' };
  }

  return null;
}

export function* postSendsV2(candidate: ISendsV2Request): Generator<any, IResponse<TSendsV2Response | IApiError>, any> {
  const recipientCount = Object.keys(candidate.recipients).length;
  const campaignName = yield select(selectCsBulkTitleCampaignName);

  const request: ISendsV2Request = {
    ...candidate,
    ...(candidate.is_delayed_shipping && !candidate.claim_link_expiration
      ? { claim_link_expiration: getDefaultExpirationDateInUTC().toISOString() }
      : {}),
    ...(recipientCount >= 2 ? { title: buildBulkSendTitle({ campaignName, recipientCount }) } : {}),
  };

  const endpoint = `${monolithApiUrl}${endpoints.sendsV2}`;
  const body = JSON.stringify(request);

  let response: IResponse<TSendsV2Response | IApiError> = yield call(AuthRequest, { method: 'POST', endpoint, body });
  let attempt = 0;

  while (
    !response.ok &&
    response.status === 409 &&
    isEngagementInProgressError((response.body as IApiError)?.message) &&
    attempt < SENDS_V2_MAX_RETRIES
  ) {
    yield delay(SENDS_V2_RETRY_DELAY_MS);
    response = yield call(AuthRequest, { method: 'POST', endpoint, body });
    attempt += 1;
  }

  return response;
}

const needsOpportunityCheck = (link: ICreateSendCrmLink | null, crmType: string | undefined): boolean =>
  Boolean(link && link.recordId && !link.opportunityId && (link.recordType === CONTACT || crmType === HUBSPOT));

export function* resolveCrmOpportunity(): Generator<any, boolean, any> {
  const link: ICreateSendCrmLink | null = yield select(selectCsCrmOpportunityTarget);
  const crmType = yield select(selectCurrentOrganizationCRMType);

  if (!needsOpportunityCheck(link, crmType)) {
    return true;
  }

  yield put(
    IntegrationsActions.getOpportunitiesRequest({ cid: (link as ICreateSendCrmLink).recordId, [CRM_TYPE]: crmType }),
  );

  const outcome = yield take([
    IntegrationsActions.GET_OPPORTUNITIES_SUCCESS,
    IntegrationsActions.GET_OPPORTUNITIES_FAILURE,
  ]);

  if (outcome.type === IntegrationsActions.GET_OPPORTUNITIES_FAILURE) {
    return false;
  }

  const opportunities: IOpportunityResponse | null = yield select(selectCrmOpportunities);

  if (!opportunities || opportunities.totalSize === 0) {
    return true;
  }

  yield put(SubmitActions.csCrmOpportunityPrompted());

  const decision = yield take([SubmitActions.CS_CRM_OPPORTUNITY_CHOSEN, SubmitActions.CS_CRM_OPPORTUNITY_CANCELLED]);

  if (decision.type === SubmitActions.CS_CRM_OPPORTUNITY_CANCELLED) {
    return false;
  }

  yield put(setCsCrmOpportunity({ uid: null, opportunityId: decision.payload.opportunityId }));

  return true;
}

export function* createSendSubmitWorker(): Generator<any, any, any> {
  try {
    const isAttributed = yield call(resolveCrmOpportunity);

    if (!isAttributed) {
      yield put(SubmitActions.csSubmitFailure(null));
      return;
    }

    const plan: TSubmitPlan | null = yield call(resolveSubmitPlan);

    if (!plan) {
      yield put(SubmitActions.csSubmitFailure(null));
      return;
    }

    if (plan.kind === 'recipients') {
      const sendsV2Response: IResponse<TSendsV2Response | IApiError> = yield call(postSendsV2, plan.engagement);

      if (!sendsV2Response.ok) {
        yield put(SubmitActions.csSubmitFailure(sendsV2Response.body));
        return;
      }

      const sendsV2Body = sendsV2Response.body as TSendsV2Response;

      if (isBulkResponse(sendsV2Body)) {
        notification.success(BULK_SEND_CREATED_MESSAGE, { content: BULK_SEND_CREATED_MESSAGE });
        yield put(routerProxyPush(routes.bulkSendManager.root));
        yield put(resetCreateSend());
        yield put(SubmitActions.csSubmitSuccess(sendsV2Body));
        return;
      }

      yield put(routerProxyPush(routes.dashboard));
      yield put(resetCreateSend());
      yield put(SubmitActions.csSubmitSuccess(sendsV2Body));
      yield put(ModalActions.toggleRocketModal(sendsV2Body, {}));
      return;
    }

    const { engagement, metadata } = plan;
    const isOneLink = plan.kind === 'link';

    if (isOneLink && !isExpirationAcceptable(engagement as IOneLinkEngagementCandidate)) {
      notification.error(EXPIRATION_OUTDATED_MESSAGE, { content: EXPIRATION_OUTDATED_MESSAGE });
      yield put(SubmitActions.csSubmitFailure(null));
      return;
    }

    const crmType = yield select(selectCurrentOrganizationCRMType);
    const endpoint = `${monolithApiUrl}${isOneLink ? endpoints.createOneLink : endpoints.engagement}`;

    const payload = mapEngagementCandidateToRequest(engagement, {
      ...metadata,
      crmType,
    });

    const response: IResponse<ICreateEngagementResponse | ICreateOneLinkSendResponse | IApiError> = yield call(
      AuthRequest,
      {
        method: 'POST',
        endpoint,
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      yield put(SubmitActions.csSubmitFailure(response.body));
      return;
    }

    yield put(routerProxyPush(routes.dashboard));
    yield put(resetCreateSend());
    yield put(SubmitActions.csSubmitSuccess(response.body as ICreateEngagementResponse | ICreateOneLinkSendResponse));
    yield put(ModalActions.toggleRocketModal(response.body, metadata));
  } catch (error) {
    yield put(SubmitActions.csSubmitFailure(error));
  }
}

const sagas = {
  *watchCreateSendSubmit(): Generator<any, any, any> {
    yield takeLatest(SubmitActions.CS_SUBMIT_REQUESTED, createSendSubmitWorker);
  },
};

export default sagas;
